import type { IntlService } from 'ember-intl';
import type { ModelRegistry } from 'ember-data/types/registries/model';

export interface IQualityFactor {
  label: string;
  percentage: number;
}

export interface IAirbnbReviewQualityItem {
  name: string;
  score: number;
  scoreColorClass: 'score-green' | 'score-orange' | 'score-red';
  topPositive: IQualityFactor[];
  topNegative: IQualityFactor[];
}

export function getScoreColorClass(
  score: number,
): 'score-green' | 'score-orange' | 'score-red' {
  if (score >= 4.7) {
    return 'score-green';
  }
  if (score >= 4.6) {
    return 'score-orange';
  }
  return 'score-red';
}

function translateTag(tagKey: string, intl: IntlService): string {
  const translationKey = tagKey.toLowerCase();
  const fullKey = `insights_airbnb.airbnb_tags.${translationKey}`;

  if (intl.exists(fullKey)) {
    const translation = intl.t(fullKey);
    if (translation !== fullKey) {
      return translation.replace(/\.$/, '');
    }
  }

  // Fallback: convert tag key to readable format
  return tagKey
    .replace(/GUEST_REVIEW_HOST_(POSITIVE|NEGATIVE)_/gi, '')
    .replace(/HOST_REVIEW_GUEST_(POSITIVE|NEGATIVE)_/gi, '')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (l) => l.toUpperCase());
}

function extractTagsFromCategory(
  tags: Record<string, number> | undefined,
  intl: IntlService,
  totalReviews: number,
): { topPositive: IQualityFactor[]; topNegative: IQualityFactor[] } {
  if (!tags || typeof tags !== 'object') {
    return { topPositive: [], topNegative: [] };
  }

  const positiveTagCounts: Record<string, number> = {};
  const negativeTagCounts: Record<string, number> = {};

  Object.entries(tags).forEach(([tagKey, count]) => {
    if (tagKey.includes('_OTHER') || typeof count !== 'number') {
      return;
    }

    if (tagKey.includes('POSITIVE')) {
      positiveTagCounts[tagKey] = count;
    } else if (tagKey.includes('NEGATIVE')) {
      negativeTagCounts[tagKey] = count;
    }
  });

  const sortedPositive = Object.entries(positiveTagCounts)
    .sort(([, countA], [, countB]) => countB - countA)
    .slice(0, 3);

  const sortedNegative = Object.entries(negativeTagCounts)
    .sort(([, countA], [, countB]) => countB - countA)
    .slice(0, 3);

  return {
    topPositive: sortedPositive
      .map(([tag, count]) => ({
        label: translateTag(tag, intl),
        percentage:
          totalReviews > 0 ? Math.round((count / totalReviews) * 100) : 0,
      }))
      .filter((item) => item.percentage > 0),
    topNegative: sortedNegative
      .map(([tag, count]) => ({
        label: translateTag(tag, intl),
        percentage:
          totalReviews > 0 ? Math.round((count / totalReviews) * 100) : 0,
      }))
      .filter((item) => item.percentage > 0),
  };
}

const CATEGORY_KEYS = [
  'cleanliness',
  'accuracy',
  'communication',
  'checkin',
  'location',
  'value',
] as const;

export function transformReviewAverageToQualityComponents(
  reviewAverage: ModelRegistry['airbnb-quality-review-average'] | undefined,
  intl: IntlService,
): IAirbnbReviewQualityItem[] {
  if (!reviewAverage) {
    return [];
  }

  return CATEGORY_KEYS.map((key) => {
    const categoryValue = reviewAverage[key] as
      | { rating: number; tags: Record<string, number> }
      | undefined;

    const score = categoryValue?.rating ? Number(categoryValue.rating) || 0 : 0;

    const { topPositive, topNegative } = extractTagsFromCategory(
      categoryValue?.tags,
      intl,
      reviewAverage.totalReviews,
    );

    const translationKey = key === 'checkin' ? 'check_in' : key;
    const categoryName =
      intl.t(`insights_airbnb.metrics.${translationKey}`) || key;

    return {
      name: categoryName,
      score: Math.round(score * 100) / 100,
      scoreColorClass: getScoreColorClass(score),
      topPositive,
      topNegative,
    };
  });
}
